import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import moment from "moment-timezone";

import Skeleton from "@mui/material/Skeleton";

import { useAuth } from "../hooks/useAuth";
import useCity from "../hooks/useCity";
import usePageTitle from "../hooks/usePageTitle";

const TEASER_COUNT = 3;
const MAX_ATTENDEES = 4;

// Small, deliberately simple teaser card for this section only.
// Not meant to replace CardSolidGame on the Dishes page — if/when
// a universal card component gets built, this is the shape to fold in.
function DishTeaser({ dish }) {
  return (
    <div className="card dish-teaser-card">
      <div
        className="dish-teaser-image"
        style={
          dish.image_url
            ? { backgroundImage: `url(${dish.image_url})` }
            : undefined
        }
      />
      <p className="dish-teaser-name">{dish.title}</p>
    </div>
  );
}

function EventTeaser({ event, authed }) {
  const attendeeCount =
    typeof event.client_count === "number"
      ? event.client_count
      : event.clients?.length;
  const spotsLeft =
    typeof attendeeCount === "number"
      ? MAX_ATTENDEES - attendeeCount
      : undefined;

  return (
    <div className="card event-teaser-row">
      <div className="event-teaser-thumb">
        {event.dish ? (
          <img src={event.dish.image_url} alt={event.dish.title} />
        ) : (
          <div
            className="event-teaser-thumb-placeholder"
            role="img"
            aria-label="Custom dish"
          >
            🍽️
          </div>
        )}
      </div>
      <div className="event-teaser-info">
        <Link to={`/events/${event.id}`} className="event-teaser-title">
          {event.title}
        </Link>
        <p className="event-teaser-time">
          {moment(event.date)
            .tz(event.timezone)
            .format(authed ? "dddd, MMMM Do · h:mm a" : "dddd, MMMM Do")}
        </p>
        {typeof spotsLeft === "number" && (
          <span className="attendee-events event-teaser-spots">
            {spotsLeft} {spotsLeft === 1 ? "spot" : "spots"} left
          </span>
        )}
        {event.tags.length > 0 && (
          <div className="chips event-teaser-tags">
            {event.tags.map((tag) => (
              <span key={tag.id} className="chip chip--display">
                {tag.title}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function City() {
  const { title } = useParams();
  const { setCity, cityId, authed } = useAuth();
  const { city, dishes, events, error, loading, dishesLoading, eventsLoading } =
    useCity(title);

  const navigate = useNavigate();

  useEffect(() => {
    if (error) {
      navigate("/cities");
    }
  }, [error, navigate]);

  usePageTitle(`${city?.title || title} | Fudopia | Meet. Eat. Repeat.`);

  const selectCity = () => {
    setCity(city.id);
  };

  const isActiveCity = city && cityId === city.id;

  const teaserDishes = dishes.slice(0, TEASER_COUNT);
  const inThreeHours = moment().add(3, "h");
  const activeEvents = events.filter((event) =>
    moment(event.date).isAfter(inThreeHours),
  );
  const teaserEvents = activeEvents.slice(0, TEASER_COUNT);

  return (
    <>
      {error && <p className="error-message">Something went wrong...</p>}
      {loading && (
        <div className="city-page">
          <Skeleton animation="wave" height={600}></Skeleton>
        </div>
      )}
      {!loading && city && (
        <div className="city-page">
          <img
            src={city.image_url}
            className="city-hero-image"
            alt={city.title}
          />

          <div className="city-hero-content">
            <h1>{title}</h1>
            <p>{city.body}</p>
          </div>

          <div className="city-section">
            <div className="city-section-header">
              <h2>Dishes to try in {title}</h2>
              {!isActiveCity && (
                <Link
                  to="/dishes"
                  className="back-button city-section-link"
                  onClick={selectCity}
                >
                  See all{" "}
                  <span className="city-section-link-arrow">&rarr;</span>
                </Link>
              )}
            </div>

            {dishesLoading && (
              <div className="city-dishes-row">
                {[...Array(TEASER_COUNT)].map((_, i) => (
                  <Skeleton
                    key={i}
                    animation="wave"
                    variant="rounded"
                    width={160}
                    height={120}
                  />
                ))}
              </div>
            )}

            {!dishesLoading && teaserDishes.length > 0 && (
              <div className="city-dishes-row">
                {teaserDishes.map((dish) => (
                  <DishTeaser key={dish.id} dish={dish} />
                ))}
              </div>
            )}

            {!dishesLoading && teaserDishes.length === 0 && (
              <p className="empty-state">
                Dishes for {city.title} are coming soon.
              </p>
            )}
          </div>

          <div className="city-section">
            <div className="city-section-header">
              <h2>Upcoming events in {title}</h2>
              {!isActiveCity && (
                <Link
                  to="/events"
                  className="back-button city-section-link"
                  onClick={selectCity}
                >
                  See all{" "}
                  <span className="city-section-link-arrow">&rarr;</span>
                </Link>
              )}
            </div>

            {eventsLoading && (
              <div className="city-events-list">
                {[...Array(TEASER_COUNT)].map((_, i) => (
                  <Skeleton
                    key={i}
                    animation="wave"
                    variant="rounded"
                    height={84}
                  />
                ))}
              </div>
            )}

            {!eventsLoading && teaserEvents.length > 0 && (
              <div className="city-events-list">
                {teaserEvents.map((event) => (
                  <EventTeaser key={event.id} event={event} authed={authed} />
                ))}
              </div>
            )}

            {!eventsLoading && teaserEvents.length === 0 && (
              <p className="empty-state">
                No upcoming events in {city.title} yet. Check back soon, or be
                the first to start one!
              </p>
            )}
          </div>

          <Link
            to="/restaurants"
            className="back-button city-restaurants-cta"
            onClick={selectCity}
          >
            Browse restaurants in {city.title}{" "}
            <span className="city-section-link-arrow">&rarr;</span>
          </Link>

          <Link to="/cities" className="back-button city-back-link">
            ← View all Cities
          </Link>
        </div>
      )}
    </>
  );
}

export default City;
